import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import MapSelector from '../Map/MapSelector';
import { subLocationsToMarkers } from '../Map/markerHelper';
import LocationCardsWrapper from '../LocationLists/LocationCardsWrapper';
import LocationCard from '../LocationLists/LocationCard';
import MoreRegionsTrailingCard from '../LocationLists/MoreRegionsTrailingCard';
import { useStandortHistory } from '../VHost/StandortHistory';
import CarMakeImage from './CarMakeImage';
import Button from '../form-elements/Button';
import { markenStandortSelector } from '../../../modules/selectors/standort/markenStandort';
import { navigateToSubLocation } from '../../../modules/actions/standort/subLocation';
import { canShowGoogleMapsSelector } from '../../../modules/selectors/oneTrust';
import { sortBy } from 'lodash';

function SubLocationFinder({ forProductDetail = false }) {
    const history = useStandortHistory();
    const canShowGoogleMaps = useSelector(canShowGoogleMapsSelector);
    const markenStandort = useSelector(markenStandortSelector);
    const [selectedCarMake, setSelectedCarMake] = useState('');
    const [showLocationList, setShowLocationList] = useState(forProductDetail);

    const allLocations = useMemo(() => {
        if (markenStandort.isAreaShop && markenStandort.areaShopSubLocations) {
            return sortBy(markenStandort.areaShopSubLocations, 'anschrift.ort');
        }
        return [markenStandort];
    }, [markenStandort]);

    const locations = useMemo(() => {
        if (selectedCarMake) {
            return allLocations.filter(location => {
                const locationCarMakes = [location.hauptFabrikat, ...location.nebenFabrikat];
                return locationCarMakes.includes(selectedCarMake) && !location.terminated;
            });
        } else {
            return allLocations.filter(location => !location.terminated);
        }
    }, [allLocations, selectedCarMake]);

    const carMakes = useMemo(
        () => [...new Set([...allLocations.map(item => item.hauptFabrikat), ...allLocations.flatMap(item => item.nebenFabrikat)])],
        [allLocations]
    );

    const markers = useMemo(() => subLocationsToMarkers({ subLocations: locations, history }), [history, locations]);

    const onCareMakeFilterChanged = useCallback(carMake => setSelectedCarMake(carMake), []);

    if (!markenStandort.implementsAreaConcept) return null;

    const logoBackgroundColor = markenStandort.theme?.body?.locationLogoBackground;

    const removeCitroenVariation = (list) => {
        if (list.includes("Citroen") && list.includes("Citroën")) {
            list = list.filter(item => item !== "Citroën");
        }
        return list;
    }

    const filteredCarMakes = removeCitroenVariation(carMakes);

    const carBrandBackgroundColors = markenStandort.carBrandBackgroundColors;

    const filteredCarMakesWithBackgroundColors = filteredCarMakes.map(carMake => {
        if (carBrandBackgroundColors && carBrandBackgroundColors[carMake]) {
            return {
                carMake,
                backgroundColor: carBrandBackgroundColors[carMake],
            };
        }
        return {
            carMake,
        };
    });

    return (
        <section className="uk-section uk-section-default uk-section-small">
            <div className="uk-container">
                {!forProductDetail && <h2 className="uk-text-light">In diesen Centern sind wir für Sie da.</h2>}
                {forProductDetail && <h4 className='uk-h3'>Unsere Standorte</h4>}
                {canShowGoogleMaps && filteredCarMakes.length && (
                    <>
                        {(!selectedCarMake && !forProductDetail) && <h3>Bitte wählen Sie eine Marke aus!</h3>}
                        {!forProductDetail &&
                            <div className="uk-tile uk-padding-small" style={{ backgroundColor: logoBackgroundColor ? logoBackgroundColor : "black" }}>
                                <div className="uk-grid uk-grid-small uk-flex uk-flex-center uk-grid-match" data-uk-grid="">
                                    {filteredCarMakesWithBackgroundColors.map(car => (
                                        <div key={car.carMake}>
                                            <Button
                                                modifier="link"
                                                className="focus-brand"
                                                onClick={() => {
                                                    onCareMakeFilterChanged(car.carMake);
                                                }}
                                            >
                                                <div
                                                    className={classnames(
                                                        'uk-tile uk-padding-small uk-height-1-1 uk-flex uk-flex-middle',
                                                        selectedCarMake === car.carMake ? 'uk-tile-primary' : 'uk-tile-default'
                                                    )}
                                                    style={selectedCarMake !== car.carMake ? { backgroundColor: car.backgroundColor || "white" } : {}}
                                                >
                                                    <CarMakeImage carMake={car.carMake} gesellschafterNr={markenStandort.gesellschafterNr} />
                                                </div>
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }
                    </>
                )}
                {!forProductDetail &&
                    <div className="uk-position-relative">
                        <MapSelector markers={markers} />
                        <div className="uk-position-bottom-right uk-position-medium">
                            <Button modifier="primary" onClick={() => setShowLocationList(!showLocationList)} ariaControls="subLocationFinderLocations" ariaExpanded={showLocationList}>
                                {showLocationList ? 'Adressen ausblenden' : 'Adressen einblenden'}
                            </Button>
                        </div>
                    </div>
                }
                <div id='subLocationFinderLocations' className='uk-magin-remove'>
                    {showLocationList && (
                        <LocationCardsWrapper>
                            {locations.map((location, index) => (
                                <div key={location._id}>
                                    <LocationCard
                                        forProductDetail={forProductDetail}
                                        location={location}
                                        positionNumber={index + 1}
                                        footerChildren={
                                            <Button
                                                modifier="secondary"
                                                fullWidth
                                                ariaLabel={`mehr Infos zum Standort ${location?.anschrift?.ort}`}
                                                onClick={() => {
                                                    navigateToSubLocation({ history, subLocationUrl: location.subLocationUrl });
                                                }}
                                            >
                                                MEHR INFOS
                                            </Button>
                                        }
                                    />
                                </div>
                            ))}
                            {markenStandort.gesellschafterAreaShopCount > 1 && (
                                <div>
                                    <MoreRegionsTrailingCard />
                                </div>
                            )}
                        </LocationCardsWrapper>
                    )}
                </div>
            </div>
        </section>
    );
}

export default SubLocationFinder;
